/**
 * Geo-Redirect MVP - Simple geolocation-based store redirection
 * Temporary solution until Cloudflare workers implementation
 */

class GeoRedirectMVP {
  constructor(settings = {}) {
    this.settings = {
      apiUrls: [
        'https://api.country.is',
        'https://ipapi.co/json/'
      ],
      apiTimeout: 4000,
      storageKey: 'geo_redirect_preference',
      storageExpiry: 30, // days
      redirectDelay: 3000, // 3 seconds
      enabled: settings.enabled || false,
      storeUrls: settings.storeUrls || {},
      currentRegion: settings.currentRegion || 'us',
      shippingCountries: settings.shippingCountries || ['us'],
      debug: settings.debug || false,
      ...settings
    };
    
    this.userPreference = this.getUserPreference();
    this.currentCountry = null;
    
    if (this.settings.debug) {
      console.log('GeoRedirectMVP initialized:', this.settings);
    }
  }
  
  /**
   * Initialize the geo-redirect system
   */
  async init() {
    if (this.settings.debug) {
      console.log('🚀 GeoRedirectMVP.init() starting', {
        settings: this.settings,
        userPreference: this.userPreference,
        currentURL: window.location.href,
        environment: window.location.hostname.includes('myshopify.com') ? 'development' : 'production'
      });
    }

    // Track geo-redirect initialization
    if (window.GTMTracking) {
      window.GTMTracking.trackGeoRedirect('geo_redirect_init', {
        enabled: this.settings.enabled,
        current_region: this.settings.currentRegion,
        user_preference: this.userPreference,
        available_stores: Object.keys(this.settings.storeUrls).length
      });
    }
    
    // Browser compatibility check
    if (!this.isBrowserSupported()) {
      if (this.settings.debug) {
        console.warn('Geo-redirect: Browser not supported, skipping initialization');
      }
      return;
    }
    
    // Skip if disabled or user has declined
    if (!this.settings.enabled || this.userPreference === 'declined') {
      if (this.settings.debug) {
        console.log('Geo-redirect: Skipped - disabled or user declined', {
          enabled: this.settings.enabled,
          userPreference: this.userPreference
        });
      }
      return;
    }
    
    // Skip if user has already chosen to stay
    if (this.userPreference === 'stay') {
      if (this.settings.debug) {
        console.log('Geo-redirect: Skipped - user chose to stay');
      }
      return;
    }
    
    // Skip in theme editor
    if (this.isThemeEditor()) {
      if (this.settings.debug) {
        console.log('Geo-redirect: Skipped - in theme editor');
      }
      return;
    }
    
    try {
      // Detect user's country with timeout
      if (this.settings.debug) {
        console.log('🌍 Starting country detection...');
      }
      
      this.currentCountry = await this.detectCountryWithTimeout();
      
      if (this.settings.debug) {
        console.log('🌍 Country detection result:', this.currentCountry);
      }
      
      if (this.currentCountry) {
        this.handleRedirect();
      } else {
        if (this.settings.debug) {
          console.warn('No country detected, geo-redirect will not proceed');
        }
      }
    } catch (error) {
      this.logError('Geo-redirect initialization failed', error);
    }
  }
  
  /**
   * Detect user's country using external IP-based APIs (skips Shopify.country for accuracy)
   */
  async detectCountryWithTimeout() {
    // Check for testing parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const testCountry = urlParams.get('geo_test');
    if (testCountry) {
      if (this.settings.debug) {
        console.log(`Using test country from URL parameter: ${testCountry}`);
      }
      return testCountry.toLowerCase();
    }

    // Skip Shopify.country detection (often inaccurate) and use external IP-based lookups directly
        if (this.settings.debug) {
      console.log('Skipping Shopify.country detection, using external APIs for accurate geo detection');
    }

    // Attempt external IP-based lookups
    for (const apiUrl of this.settings.apiUrls) {
      try {
        if (this.settings.debug) {
          console.log('Fetching country via API:', apiUrl);
        }
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.settings.apiTimeout);
        const resp = await fetch(apiUrl, { signal: controller.signal });
        clearTimeout(timer);
        if (resp.ok) {
          const data = await resp.json();
          const code = data.country || data.country_code || data.countryCode;
          if (code) {
            if (this.settings.debug) {
              console.log(`Using API country: ${code}`);
            }
            return code.toLowerCase();
          }
        } else if (this.settings.debug) {
          console.warn('Geo API response not OK:', resp.status);
        }
      } catch (error) {
        if (this.settings.debug) {
          console.warn('Geo API lookup failed:', error);
        }
      }
    }

    // Fallback: null if not available
    if (this.settings.debug) {
      console.warn('No country detected (all detection methods failed)');
    }
    return null;
  }

  // Deprecated: Apple Pay country detection caused incorrect results on some stores
  getApplePayCountry() {
    return null;
  }
  
  /**
   * Handle redirect logic based on detected country
   */
  handleRedirect() {
    if (this.settings.debug) {
      console.log('🔄 Handling redirect for country:', this.currentCountry, {
        shippingCountries: this.settings.shippingCountries,
        currentRegion: this.settings.currentRegion,
        storeUrls: this.settings.storeUrls
      });
    }
    
    // Check if user is already in correct region
    if (this.isUserInShippingZone()) {
      if (this.settings.debug) {
        console.log('✅ User is in correct shipping zone, no redirect needed');
      }
      return;
    }

    // If country is shipping restricted, show restriction modal with correct label
    if (this.isShippingRestricted(this.currentCountry)) {
      const countryLabel = this.getCountryLabel(this.currentCountry);
      if (this.settings.debug) {
        console.log('🚫 Country is shipping restricted, showing restriction modal', {
          country: this.currentCountry,
          label: countryLabel
        });
      }
      this.showRedirectNotification({
        url: '',
        country: this.currentCountry,
        label: countryLabel
      });
      return;
    }

    // Find appropriate store for user's country
    const suggestedStore = this.findStoreForCountry(this.currentCountry);
    
    if (this.settings.debug) {
      console.log('🔍 Store lookup result:', suggestedStore);
    }

    if (suggestedStore) {
      if (this.settings.debug) {
        console.log('✅ Showing redirect notification for store:', suggestedStore);
      }
      this.showRedirectNotification(suggestedStore);
    } else {
      if (this.settings.debug) {
        console.warn('⚠️ No appropriate store found for country:', this.currentCountry);
      }
    }
  }
  
  /**
   * Check if user is in current store's shipping zone
   */
  isUserInShippingZone() {
    return this.settings.shippingCountries.includes(this.currentCountry);
  }
  
  /**
   * Find appropriate store URL for given country
   */
  findStoreForCountry(countryCode) {
    // Direct country match
    if (this.settings.storeUrls[countryCode]) {
      return {
        url: this.settings.storeUrls[countryCode],
        country: countryCode,
        label: this.getCountryLabel(countryCode)
      };
    }
    
    // Regional fallbacks
    const regionMap = {
      'gb': 'uk',
      'ie': 'uk',
      'at': 'de',
      'ch': 'de',
      'be': 'eu',
      'nl': 'eu',
      'it': 'eu',
      'pt': 'eu'
    };
    
    const regionCode = regionMap[countryCode];
    if (regionCode && this.settings.storeUrls[regionCode]) {
      return {
        url: this.settings.storeUrls[regionCode],
        country: regionCode,
        label: this.getCountryLabel(regionCode)
      };
    }
    
    return null;
  }
  
  /**
   * Get human-readable country label
   */
  getCountryLabel(countryCode) {
    const labels = {
      'us': 'United States',
      'ca': 'Canada',
      'uk': 'United Kingdom',
      'gb': 'United Kingdom',
      'eu': 'Europe',
      'de': 'Germany',
      'fr': 'France',
      'es': 'Spain',
      'au': 'Australia',
      'nz': 'New Zealand',
      'ch': 'Switzerland',
      'ie': 'Ireland',
      'no': 'Norway',
      // Add more as needed
    };
    return labels[(countryCode || '').toLowerCase()] || countryCode.toUpperCase();
  }
  
  /**
   * Show redirect notification to user
   */
  showRedirectNotification(store) {
    // Track notification display
    if (window.GTMTracking) {
      const isRestricted = this.isShippingRestricted(this.currentCountry);
      window.GTMTracking.trackGeoRedirect('geo_redirect_notification_shown', {
        detected_country: this.currentCountry,
        suggested_store: store.label || store.country,
        store_url: store.url,
        is_shipping_restricted: isRestricted,
        current_region: this.settings.currentRegion,
        notification_type: isRestricted ? 'shipping_restriction' : 'store_suggestion'
      });
    }

    // Create modal overlay
    const overlay = this.createNotificationElement(store);
    
    // Set initial state
    overlay.setAttribute('aria-hidden', 'false');
    
    // Add to page
    document.body.appendChild(overlay);
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Show after brief delay for smooth animation
    setTimeout(() => {
      overlay.classList.add('show');
    }, 10);
    
    // Auto-redirect after delay if no user interaction (unless disabled or restricted country)
    let autoRedirectTimer = null;
    const isRestricted = this.isShippingRestricted(this.currentCountry);
    if (!this.settings.disableAutoRedirect && !isRestricted && store.url) {
      autoRedirectTimer = setTimeout(() => {
        this.redirectToStore(store.url);
      }, this.settings.redirectDelay);
    }
    
    // Setup event listeners
    this.setupNotificationEvents(overlay, store, autoRedirectTimer);
  }
  
  /**
   * Create notification DOM element
   */
  createNotificationElement(store) {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'geo-redirect-overlay';
    
    // Create modal container
    const modal = document.createElement('div');
    modal.className = 'geo-redirect-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'geo-redirect-title');
    modal.setAttribute('aria-describedby', 'geo-redirect-description');
    
    const isRestricted = this.isShippingRestricted(this.currentCountry);
    const countryLabel = this.getCountryLabel(this.currentCountry);
    modal.innerHTML = `
      <div class="geo-redirect-content">
        <div class="geo-redirect-header">
          <button class="button button--tertiary geo-redirect-close-btn" data-action="close" aria-label="Close">
            <span>&times;</span>
          </button>
        </div>
        <div class="geo-redirect-message rte">
          ${isRestricted ? `
            <p>${(window.themeSettings?.shippingRestrictionMessage || 'We currently do not ship to your region. Please check our stockist page.').replace('your region', countryLabel)}</p>
            <a href="${window.themeSettings?.stockistPageUrl || '/pages/find-stockist'}">Find a stockist</a>
          ` : `
            <h3 id="geo-redirect-title">We've detected you're in ${store.label}</h3>
            <p id="geo-redirect-description">Would you like to visit our ${store.label} store for local pricing and faster shipping?</p>
          `}
        </div>
        <div class="geo-redirect-actions">
          ${!isRestricted ? `<button class="button button--full-width button--primary geo-redirect-accept" data-action="accept">
            <span>Visit ${(store.country ? store.country.toUpperCase() : store.label)} store</span>
          </button>` : ''}
          <button class="button button--full-width button--secondary geo-redirect-decline" data-action="decline">
            <span>Stay here</span>
          </button>
        </div>
      </div>
    `;
     
    // Append modal to overlay
    overlay.appendChild(modal);
    
    // Reset button states to prevent stuck spinner on back navigation
    const acceptBtn = modal.querySelector('.geo-redirect-accept');
    if (acceptBtn) {
      acceptBtn.classList.remove('loading');
      acceptBtn.disabled = false;
    }
    const declineBtn = modal.querySelector('.geo-redirect-decline');
    if (declineBtn) {
      declineBtn.classList.remove('loading');
      declineBtn.disabled = false;
    }
    
    // Add CSS styles
    this.addNotificationStyles();
    
    return overlay;
  }
  
  /**
   * Add CSS styles for notification
   */
  addNotificationStyles() {
    if (document.getElementById('geo-redirect-styles')) {
      return;
    }
    
    const styles = document.createElement('style');
    styles.id = 'geo-redirect-styles';
    styles.textContent = `
      /* Modal Overlay */
      .geo-redirect-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        box-sizing: border-box;
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      
      .geo-redirect-overlay.show {
        opacity: 1;
      }
      
      /* Modal Container */
      .geo-redirect-modal {
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        max-width: 550px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        transform: scale(0.95);
        transition: transform 0.3s ease;
        position: relative;
      }
      
      .geo-redirect-overlay.show .geo-redirect-modal {
        transform: scale(1);
      }
      
      /* Modal Content */
      .geo-redirect-content {
        padding: 52px 50px 24px;
        text-align: center;
      }
      
      /* Modal Header */
      .geo-redirect-header {
        position: absolute;
        top: 16px;
        right: 16px;
      }
      
             .geo-redirect-close-btn {
         width: auto;
         min-width: auto;
         min-height: auto;
         padding: 8px;
         font-size: 24px;
         line-height: 1;
         border-radius: 50%;
       }
       
       .geo-redirect-close-btn span {
         display: flex;
         align-items: center;
         justify-content: center;
         width: 24px;
         height: 24px;
       }
      
      /* Modal Message */
      .geo-redirect-message {
        margin-bottom: 24px;
      }
      
      .geo-redirect-message h3 {
        margin: 0 0 12px 0;
        font-size: 20px;
        font-weight: 900;  
        color: #333;
        line-height: 1.3;
      }
      
      .geo-redirect-message p {
        margin: 0;
        font-size: 16px;
        color: #666;
        line-height: 1.5;
      }
      
             /* Modal Actions */
       .geo-redirect-actions {
         display: flex;
         gap: 12px;
         flex-direction: column;
       }
       
       /* Override any theme button styles if needed for modal context */
       .geo-redirect-actions .button {
         order: 1; /* Accept button first */
       }
       
       .geo-redirect-actions .geo-redirect-decline {
         order: 2; /* Decline button second */
       }
      
      /* Responsive Design */
      @media (min-width: 480px) {
        .geo-redirect-actions {
          flex-direction: row;
        }
        
        .geo-redirect-accept {
          order: 1;
          flex: 1;
        }
        
        .geo-redirect-decline {
          order: 2;
          flex: 1;
        }
      }
      
      @media (max-width: 479px) {
        .geo-redirect-overlay {
          padding: 16px;
        }
        
        .geo-redirect-content {
          padding: 54px 50px 30px;
        }
        
        .geo-redirect-message h3 {
          font-size: 18px;
        }
        
        .geo-redirect-message p {
          font-size: 15px;
        }
        
                 .geo-redirect-actions .button {
           font-size: 15px;
           min-height: 44px; /* iOS touch target minimum */
         }
      }
      
             /* Accessibility improvements */
       @media (prefers-reduced-motion: reduce) {
         .geo-redirect-overlay,
         .geo-redirect-modal,
         .geo-redirect-actions .button,
         .geo-redirect-close-btn {
           transition: none;
         }
       }
       
       /* High contrast mode support */
       @media (prefers-contrast: high) {
         .geo-redirect-modal {
           border: 2px solid;
         }
       }
      
      /* Focus trap styling */
      .geo-redirect-overlay[aria-hidden="false"] {
        pointer-events: auto;
      }
      
      .geo-redirect-overlay[aria-hidden="true"] {
        pointer-events: none;
      }
      
      .shipping-restriction-message {
        margin-top: 15px;
        padding: 10px;
        background-color: #f8f8f8;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
      }
      .shipping-restriction-message a {
        text-decoration: underline;
      }
    `;
    
    document.head.appendChild(styles);
  }
  
  /**
   * Show loading spinner on button
   */
  showLoadingSpinner(button) {
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
      button.classList.add('loading');
      button.disabled = true;
    }
  }
  
  /**
   * Hide loading spinner on button
   */
  hideLoadingSpinner(button) {
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  /**
   * Setup event listeners for notification
   */
  setupNotificationEvents(overlay, store, autoRedirectTimer) {
    const modal = overlay.querySelector('.geo-redirect-modal');
    
    // Handle button clicks
    overlay.addEventListener('click', (e) => {
      // Check if the clicked element or its parent is a button with an action
      let button = e.target.closest('[data-action]');
      if (!button) return;
      
      const action = button.dataset.action;
      
      if (action === 'accept') {
        // Track user acceptance
        if (window.GTMTracking) {
          window.GTMTracking.trackGeoRedirect('geo_redirect_accepted', {
            detected_country: this.currentCountry,
            selected_store: store.label || store.country,
            store_url: store.url,
            current_region: this.settings.currentRegion,
            interaction_type: 'button_click'
          });
        }
        
        if (!store.url) {
          this.hideLoadingSpinner(button);
          alert('No store available for your region.');
          return;
        }
        this.showLoadingSpinner(button);
        if (autoRedirectTimer) clearTimeout(autoRedirectTimer);
        this.setUserPreference('accepted');
        // Small delay to show spinner before redirect
        setTimeout(() => {
          this.redirectToStore(store.url);
        }, 100);
      } else if (action === 'decline') {
        // Track user decline
        if (window.GTMTracking) {
          window.GTMTracking.trackGeoRedirect('geo_redirect_declined', {
            detected_country: this.currentCountry,
            suggested_store: store.label || store.country,
            current_region: this.settings.currentRegion,
            interaction_type: 'button_click'
          });
        }
        
        this.showLoadingSpinner(button);
        if (autoRedirectTimer) clearTimeout(autoRedirectTimer);
        this.setUserPreference('stay');
        // Small delay to show spinner before hiding
        setTimeout(() => {
          this.hideNotification(overlay);
        }, 100);
      } else if (action === 'close') {
        // Track user close
        if (window.GTMTracking) {
          window.GTMTracking.trackGeoRedirect('geo_redirect_closed', {
            detected_country: this.currentCountry,
            suggested_store: store.label || store.country,
            current_region: this.settings.currentRegion,
            interaction_type: 'close_button'
          });
        }
        
        if (autoRedirectTimer) clearTimeout(autoRedirectTimer);
        this.setUserPreference('declined');
        this.hideNotification(overlay);
      }
    });
       
    // Click outside modal to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        // Track click outside
        if (window.GTMTracking) {
          window.GTMTracking.trackGeoRedirect('geo_redirect_dismissed', {
            detected_country: this.currentCountry,
            suggested_store: store.label || store.country,
            current_region: this.settings.currentRegion,
            interaction_type: 'click_outside'
          });
        }
        
        if (autoRedirectTimer) clearTimeout(autoRedirectTimer);
        this.setUserPreference('declined');
        this.hideNotification(overlay);
      }
    });
    
    // Escape key to close
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // Track escape key
        if (window.GTMTracking) {
          window.GTMTracking.trackGeoRedirect('geo_redirect_dismissed', {
            detected_country: this.currentCountry,
            suggested_store: store.label || store.country,
            current_region: this.settings.currentRegion,
            interaction_type: 'escape_key'
          });
        }
        
        if (autoRedirectTimer) clearTimeout(autoRedirectTimer);
        this.setUserPreference('declined');
        this.hideNotification(overlay);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    // Focus management
    this.setupFocusTrap(modal);
  }
  
  /**
   * Setup focus trap for modal accessibility
   */
  setupFocusTrap(modal) {
    const focusableElements = Array.from(modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ));
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Do not set any initial focus

    // Trap focus
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    });
  }
  
  /**
   * Hide notification with animation
   */
  hideNotification(overlay) {
    // Set aria-hidden for accessibility
    overlay.setAttribute('aria-hidden', 'true');
    
    // Remove show class to trigger hide animation
    overlay.classList.remove('show');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Remove from DOM after animation
    setTimeout(() => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }, 300);
  }
  
  /**
   * Redirect to store URL
   */
  redirectToStore(url) {
    // Preserve current path and query parameters
    const currentPath = window.location.pathname + window.location.search;
    const redirectUrl = url.replace(/\/$/, '') + currentPath;
    
    if (this.settings.debug) {
      console.log('Redirecting to:', redirectUrl);
    }
    
    window.location.href = redirectUrl;
  }
  
  /**
   * Check if browser supports required features
   */
  isBrowserSupported() {
    try {
      // Check for essential features
      const hasLocalStorage = typeof Storage !== 'undefined' && window.localStorage;
      const hasFetch = typeof window.fetch === 'function';
      const hasPromise = typeof Promise !== 'undefined';
      const hasJSON = typeof JSON !== 'undefined' && JSON.parse && JSON.stringify;
      const hasQuerySelector = typeof document.querySelector === 'function';
      const hasAddEventListener = typeof EventTarget !== 'undefined' && EventTarget.prototype.addEventListener;
      
      return hasLocalStorage && hasFetch && hasPromise && hasJSON && hasQuerySelector && hasAddEventListener;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Log errors with enhanced information
   */
  logError(message, error, context = {}) {
    const errorInfo = {
      message,
      error: error ? error.toString() : 'Unknown error',
      stack: error && error.stack ? error.stack : null,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      context
    };
    
    if (this.settings.debug) {
      console.error('GeoRedirectMVP Error:', errorInfo);
    }
    
    // In production, you might want to send this to an error tracking service
    // Example: sendErrorToService(errorInfo);
  }
  
  /**
   * Check if in Shopify theme editor
   */
  isThemeEditor() {
    return window.parent !== window || 
           window.location.search.includes('preview_theme_id') ||
           document.querySelector('body.theme-editor');
  }
  
  /**
   * Get user preference from localStorage
   */
  getUserPreference() {
    try {
      // Check if localStorage is available
      if (!this.isLocalStorageAvailable()) {
        return null;
      }
      
      const stored = localStorage.getItem(this.settings.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        const now = new Date().getTime();
        
        // Validate data structure
        if (!data || typeof data !== 'object' || !data.hasOwnProperty('value')) {
          localStorage.removeItem(this.settings.storageKey);
          return null;
        }
        
        // Check if expired
        if (data.expires && now > data.expires) {
          localStorage.removeItem(this.settings.storageKey);
          return null;
        }
        
        return data.value;
      }
    } catch (error) {
      this.logError('Error reading user preference', error);
      // Clean up potentially corrupted data
      try {
        localStorage.removeItem(this.settings.storageKey);
      } catch (cleanupError) {
        // Silent fail on cleanup
      }
    }
    
    return null;
  }
  
  /**
   * Set user preference in localStorage
   */
  setUserPreference(value) {
    try {
      // Check if localStorage is available
      if (!this.isLocalStorageAvailable()) {
        this.logError('localStorage not available', null, { value });
        return false;
      }
      
      const expires = new Date().getTime() + (this.settings.storageExpiry * 24 * 60 * 60 * 1000);
      const data = {
        value: value,
        expires: expires,
        timestamp: new Date().getTime()
      };
      
      localStorage.setItem(this.settings.storageKey, JSON.stringify(data));
      return true;
    } catch (error) {
      this.logError('Error saving user preference', error, { value });
      return false;
    }
  }
  
  /**
   * Check if localStorage is available and working
   */
  isLocalStorageAvailable() {
    try {
      const test = '__geo_redirect_test__';
      localStorage.setItem(test, 'test');
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Reset user preferences (for testing)
   */
  resetPreferences() {
    localStorage.removeItem(this.settings.storageKey);
  }
  
  /**
   * Check if a country is shipping restricted
   */
  isShippingRestricted(country) {
    // Get restricted countries from theme settings, fallback to default
    const restrictedCountriesString = window.themeSettings?.shippingRestrictedCountries || 'ch,ie,no';
    const restricted = restrictedCountriesString.toLowerCase().split(',').map(c => c.trim());
    return restricted.includes((country || '').toLowerCase());
  }
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GeoRedirectMVP;
} else {
  window.GeoRedirectMVP = GeoRedirectMVP;
} 